import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { T, useTranslate } from '@tolgee/react';
import {
	SypacButton,
	SypacTabs,
	SypacText,
} from '@sypac/component-library-react';
import SearchBar from '../../../components/SearchBar/SearchBar';
import { MutatingDots } from 'react-loader-spinner';
import Pagination from '../../../components/Pagination/Pagination';
import { ContractsTable } from '../../../components/ContractsTable/ContractsTable';
import { PlusIcon } from '../../../assets/PlusIcon';
import { Sorting } from '../../../components/Sorting/Sorting';
import { SortingItemInterface } from '../../../components/Sorting/Sorting.interface';
import ContractsDetails from '../../../components/ContractsTable/components/ContractsDetails';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LIMIT } from '../../../constants';
import {
	Contract,
	ContractsService,
	GetContractsQueryParams,
} from '../../../services/contracts.service';
import { toastVariant } from '../../../components/ToastVariant/toastVariant';
import SypacToast from '../../../assets/SypacToast';
import { TabsButton } from '../../../components/TabsButton/TabsButton';
import { OrderContext } from '../../../context/OrderContext/order.context';
import { OrderTarget } from '../../../components/OrdersTable/interfaces/OrderStatus.interface';
import CountryBookTabs from '../../../components/CountryBookTabs/CountryBookTabs';
import { CountryTab } from '../../../components/CountryBookTabs/CountryBookTabs.interface';
import OrderDetails from '../../Admin/OrderDetails';
import { OrdersService } from '../../../services/orders.services';
import { OrderInterface } from '../../../components/OrdersTable/interfaces/Order.interface';
import { ContractStatus } from '../../../components/ContractsTable/interfaces/Contract.interface';
import CompanyDetails from '../ComanyDetails';

const sortOptions = [
	{ title: 'Price low to high', value: 'priceLowToHigh' },
	{ title: 'Price high to low', value: 'priceHighToLow' },
	{ title: 'Date low to high', value: 'dateLowToHigh' },
	{ title: 'Date high to low', value: 'dateHighToLow' },
];

const COUNTRY_TABS: Record<string, CountryTab> = {
	all: {
		key: 'all',
		label: 'adminProducts.allCountries',
		translatedLabel: 'All countries',
	},
	PL: {
		key: 'POL',
		label: 'adminProducts.poland',
		translatedLabel: 'Poland',
		flag: 'PL',
	},
	MD: {
		key: 'MDA',
		label: 'adminProducts.moldova',
		translatedLabel: 'Moldova',
		flag: 'MD',
	},
} as const;

const Contracts: React.FC = () => {
	const { t } = useTranslate();
	const location = useLocation();
	const detailsRef = useRef<HTMLDivElement>(null);
	const companyModalRef = useRef<HTMLDivElement | null>(null);
	const navigate = useNavigate();
	const { additional } = useContext(OrderContext);
	const { contractId } = useParams<{ contractId?: string }>();
	const hasConsumedOpenFromStateRef = useRef<string | null>(null);

	const [contracts, setContracts] = useState<Contract[]>([]);
	const [page, setPage] = useState<number>(0);
	const [count, setCount] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(false);

	const [statuses, setStatuses] = useState<string[]>([]);
	const [selectedTab, setSelectedTab] = useState('all');
	const [countryTab, setCountryTab] = useState<string>('all');
	const [selectedContract, setSelectedContract] = useState<Contract>();
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [showDetails, setShowDetails] = useState<boolean>(true);
	const [sorting, setSorting] = useState<SortingItemInterface>();
	const [isOrderOpen, setIsOrderOpen] = useState<boolean>(false);
	const [selectedOrderDetails, setSelectedOrderDetails] = useState<
		OrderInterface | undefined
	>();
	const [isCompanyOpen, setIsCompanyOpen] = useState<boolean>(false);
	const [selectedCompanyId, setSelectedCompanyId] = useState<
		number | undefined
	>();
	const toastScheduledRef = useRef(false);

	const sortToApi = useCallback(
		(
			s?: SortingItemInterface,
		):
			| Pick<GetContractsQueryParams, 'orderBy' | 'orderDirection'>
			| undefined => {
			if (!s?.value) return undefined;

			switch (s.value) {
				case 'priceLowToHigh':
					return { orderBy: 'pricePerTonBuyer', orderDirection: 'ASC' };
				case 'priceHighToLow':
					return { orderBy: 'pricePerTonBuyer', orderDirection: 'DESC' };
				case 'dateLowToHigh':
					return { orderBy: 'expiresAt', orderDirection: 'ASC' };
				case 'dateHighToLow':
					return { orderBy: 'expiresAt', orderDirection: 'DESC' };
				default:
					return undefined;
			}
		},
		[],
	);

	const getContractsCallback = useCallback(async () => {
		setLoading(true);
		try {
			const sortParams = sortToApi(sorting);

			const query: GetContractsQueryParams = {
				limit: LIMIT,
				offset: page * LIMIT,
				...sortParams,
				...(countryTab !== 'all' ? { countryCode: countryTab } : {}),
				...(statuses.length ? { statuses } : {}),
				...(searchQuery ? { search: searchQuery } : {}),
			};

			const { data } = await ContractsService.getContracts(query);

			setContracts(data.items || []);
			setCount(data.count || 0);
		} catch (e) {
		} finally {
			setLoading(false);
		}
	}, [page, sorting, sortToApi, countryTab, statuses, searchQuery]);

	const setCurrentTab = (statuses: string[]) => {
		setPage(0);
		setStatuses(statuses);
	};

	const openCompanyDetails = useCallback((companyId: number) => {
		setSelectedCompanyId(companyId);
		setIsCompanyOpen(true);
	}, []);

	const closeCompanyDetails = useCallback(() => {
		setIsCompanyOpen(false);
		setSelectedCompanyId(undefined);
	}, []);

	const handleContractClick = useCallback(
		async (contract: Contract, shouldRefetch = false) => {
			try {
				closeCompanyDetails();

				setIsOrderOpen(false);
				setSelectedOrderDetails(undefined);

				setSelectedContract(undefined);
				const { data } = await ContractsService.getContractById(contract.id);
				setSelectedContract(data);
				setShowDetails(true);

				if (shouldRefetch) {
					await getContractsCallback();
				}
			} catch (e) {}
		},
		[getContractsCallback, closeCompanyDetails],
	);

	const handleContractUpdate = useCallback(
		async (updated: Contract) => {
			setSelectedContract(updated);
			await getContractsCallback();
		},
		[getContractsCallback],
	);

	const handleSorting = (sort: SortingItemInterface) => {
		setSorting(sort);
		setPage(0);
	};

	const closeModal = () => {
		setSelectedContract(undefined);
		setShowDetails(false);
	};

	const openOrderDetails = useCallback(async (orderId: number) => {
		try {
			const { data } = await OrdersService.getOrder(orderId, OrderTarget.admin);
			setSelectedOrderDetails(data as unknown as OrderInterface);
			setShowDetails(true);
			setIsOrderOpen(true);
		} catch (e) {}
	}, []);

	const closeOrderDetails = useCallback(() => {
		setIsOrderOpen(false);
		setSelectedOrderDetails(undefined);
	}, []);

	const clickOutsideDetails = useCallback((event: MouseEvent) => {
		const target = event.target as HTMLElement | null;
		if (!target) return;
		if (target.closest('.modal-overlay')) {
			return;
		}

		if (
			!detailsRef.current?.contains(target) &&
			!additional.orderModalRef.current?.contains(target) &&
			!companyModalRef.current?.contains(target)
		) {
			closeModal();
			closeCompanyDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleSearchChange = useCallback((q?: string) => {
		const normalized = (q ?? '').trim();
		setSearchQuery(normalized);
		setPage(0);
	}, []);

	useEffect(() => {
		document.addEventListener('mousedown', clickOutsideDetails);
		return () => {
			document.removeEventListener('mousedown', clickOutsideDetails);
		};
	}, [clickOutsideDetails]);

	useEffect(() => {
		getContractsCallback().then(() => {});
	}, [getContractsCallback]);

	useEffect(() => {
		if (toastScheduledRef.current || loading) return;

		const shouldToast =
			sessionStorage.getItem('contractCreated') ||
			(location.state && location.state.contractCreated);

		if (!shouldToast) return;

		toastScheduledRef.current = true;

		sessionStorage.removeItem('contractCreated');
		if (location.state?.contractCreated) {
			navigate(location.pathname, { replace: true, state: null });
		}

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				toastVariant('Contract successfully created!', false, undefined, {
					className: 'bg-emerald-600 text-white rounded-xl',
					progressClassName: 'bg-white',
					icon: (
						<div className="w-[24px] h-[24px]">
							<SypacToast width={24} height={24} />
						</div>
					),
					style: {
						minHeight: 'auto',
						padding: 0,
						border: '1px solid #E8E8E8',
						borderRadius: 8,
					},
				});
			});
		});
	}, [loading, location.pathname, location.state, navigate]);

	useEffect(() => {
		const idFromState = (location.state as { openContractId?: number } | null)
			?.openContractId;
		const idFromParam = contractId ? parseInt(contractId, 10) : undefined;
		const targetId = idFromState ?? idFromParam;

		if (!targetId || Number.isNaN(targetId) || loading) return;

		if (hasConsumedOpenFromStateRef.current === location.key) return;
		hasConsumedOpenFromStateRef.current = location.key;

		(async () => {
			const existing = contracts.find((c) => c.id === targetId);
			if (existing) {
				await handleContractClick(existing);
			} else {
				try {
					const { data } = await ContractsService.getContractById(targetId);
					closeCompanyDetails();

					setContracts((prev) =>
						prev.find((c) => c.id === targetId) ? prev : [data, ...prev],
					);
					setSelectedContract(data);
					setShowDetails(true);
				} catch (e) {}
			}

			try {
				const s = window.history.state;
				if (s && s.usr && 'openContractId' in s.usr) {
					const nextUsr = { ...s.usr };
					delete nextUsr.openContractId;
					window.history.replaceState({ ...s, usr: nextUsr }, '');
				}
			} catch {}
		})();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.key, loading, contracts, handleContractClick, contractId]);

	return (
		<div className="relative flex flex-col gap-5 w-[calc(100vw-67px)] xl-2xl:w-[calc(100vw-83px)] h-full">
			<div className="hidden xl:flex flex-col gap-4">
				<div className="hidden xl:flex items-center mx-[30px]">
					<SypacText variant="heading-4">
						<p className="text-gray-80 mt-[6px]">
							<T keyName="adminContracts.contracts">Contracts</T>
						</p>
					</SypacText>
					<div className="ml-auto">
						<SypacButton variant="subTitle">
							<button
								type="button"
								className="w-fit p-0 m-0 flex justify-center items-center gap-3 border-0 group"
								onClick={() => navigate('/contracts/new')}
							>
								<div className="w-[24px] h-[24px] flex items-center justify-center transition bg-mountain-meadow group-hover:bg-mountain-meadow/80 rounded-[6px]">
									<PlusIcon color="white" />
								</div>
								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[16px] leading-[22px] transition group-hover:text-gray-80/60">
										<T keyName="adminContracts.createContract">
											Create contract
										</T>
									</p>
								</SypacText>
							</button>
						</SypacButton>
					</div>
				</div>

				<CountryBookTabs
					tabs={COUNTRY_TABS}
					activeTab={countryTab}
					onTabChange={setCountryTab}
				/>
			</div>

			<div className="flex items-center mx-[30px]">
				<SypacTabs className="flex w-fit p-[3px] gap-[4px]">
					{[
						{ label: t('orders.all', 'All'), buttonId: 'all', statuses: [] },
						{
							label: t('ordersList.toDo', 'To do'),
							buttonId: 'to_do',
							statuses: [ContractStatus.to_do],
						},
						{
							label: t('ordersList.progress', 'In progress'),
							buttonId: 'progress',
							statuses: [ContractStatus.in_progress],
						},
						{
							label: t('ordersList.completed', 'Completed'),
							buttonId: 'completed',
							statuses: [ContractStatus.completed],
						},
						{
							label: t('ordersList.expired', 'Expired'),
							buttonId: 'expired',
							statuses: [ContractStatus.expired],
						},
						{
							label: t('ordersList.canceled', 'Canceled'),
							buttonId: 'canceled',
							statuses: [ContractStatus.canceled],
						},
					].map(({ label, buttonId, statuses }) => (
						<TabsButton
							key={buttonId}
							label={label}
							buttonId={buttonId}
							onClick={() => setCurrentTab(statuses)}
							isSelected={selectedTab === buttonId}
							handleSelectedTab={() => setSelectedTab(buttonId)}
						/>
					))}
				</SypacTabs>

				<div className="flex gap-[40px] items-center ml-auto">
					<Sorting
						options={sortOptions}
						action={(item) => handleSorting(item)}
						current={sorting}
						label={{ title: 'Sorting filter here:', key: 'sortingFilterHere' }}
						customDefault={{ title: 'All', key: 'all' }}
						forContracts
						contractsDateSectionLabel={{
							title: 'Sort by expiration date',
							keyName: 'adminContracts.sortByExpirationDate',
						}}
					/>

					<SearchBar
						placeholder={t(
							'adminContracts.searchBy',
							'Search by Product name or ID',
						)}
						onChange={handleSearchChange}
						showButton={false}
						classNames="w-[288px]"
					/>
				</div>
			</div>

			<section className="w-full h-[calc(100%-119px)] xl:h-full min-h-0">
				{loading ? (
					<div className="flex w-full h-full items-center justify-center">
						<MutatingDots
							height="100"
							width="100"
							color="#7693F4"
							secondaryColor="#494C83"
							radius="12.5"
							ariaLabel="mutating-dots-loading"
							visible
						/>
					</div>
				) : (
					<div className="h-full relative ml-5 mr-7.5 xl:mx-[30px] border border-solid border-gray-10 rounded-10 overflow-hidden whitespace-nowrap">
						<div
							className={`w-full overflow-y-auto scroll-smooth pr-[5px] ${
								contracts.length ? 'h-[calc(100%-56px)]' : 'h-[calc(100%+2px)]'
							}`}
							style={{ scrollbarGutter: 'stable' }}
						>
							<ContractsTable
								rows={contracts}
								rowClick={handleContractClick}
								search={searchQuery}
								clickedOutside={!showDetails}
								selectedContractId={selectedContract?.id}
								role={OrderTarget.admin}
								onCompanyClick={openCompanyDetails}
								currentTab={selectedTab}
							/>
						</div>

						{contracts.length ? (
							<div className="w-full absolute bottom-0 z-10 border border-solid border-gray-10 border-l-0 border-r-0 border-b-0 shadow-pagination bg-white">
								<div className="flex justify-between items-center h-[56px] px-4">
									<Pagination
										showText={true}
										count={count}
										page={page}
										onClick={setPage}
									/>
								</div>
							</div>
						) : null}
					</div>
				)}
			</section>

			{selectedContract && showDetails && (
				<div
					ref={detailsRef}
					className="fixed inset-y-0 right-0 z-[70] w-[716px] max-w-[90vw] bg-white border-l border-gray-10 shadow-xl"
					role="dialog"
					aria-modal="true"
				>
					<ContractsDetails
						contract={selectedContract}
						role={OrderTarget.admin}
						onOrderClick={openOrderDetails}
						onContractUpdate={handleContractUpdate}
						onCompanyClick={openCompanyDetails}
					/>
				</div>
			)}

			{isCompanyOpen && selectedCompanyId !== undefined && (
				<div ref={companyModalRef}>
					<CompanyDetails
						onClose={closeCompanyDetails}
						companyId={selectedCompanyId}
						onVerification={closeCompanyDetails}
						fromContracts
					/>
				</div>
			)}

			{isOrderOpen && selectedOrderDetails?.id && (
				<div
					ref={additional.orderModalRef}
					className="fixed inset-y-0 right-0 z-[120] w-[716px] max-w-[90vw]"
					onMouseDown={(e) => {
						e.stopPropagation();
						e.nativeEvent?.stopImmediatePropagation?.();
					}}
					onClick={(e) => {
						e.stopPropagation();
						e.nativeEvent?.stopImmediatePropagation?.();
					}}
				>
					<OrderDetails
						isOpen={true}
						selectedOrder={selectedOrderDetails}
						position="absolute"
						onBack={closeOrderDetails}
						hideViewContractButton={true}
					/>
				</div>
			)}
		</div>
	);
};

export default Contracts;
